import React, { useState } from 'react';
import { Pane, Paragraph, TextInput, Checkbox, Button, PlusIcon, toaster } from 'evergreen-ui';
import ExpandablePane from "./ExpandablePane";
import DropFile from "./DropFile";
import ConversionSettings from "./ConversionSettings";
import Note from "./Note";
import ReplacePage from "./ReplacePage";
import InsertPage from "./InsertPage";
import MetadataForm from "./MetadataForm";
import VersionForm from "./VersionForm";
import CustomPages from "./CustomPages";
import env from "react-dotenv";

const ManualUpload = (props) => {
  const { requestWithAuth } = props;

    return (
      <ExpandablePane 
        expanded={false} 
        label="Manual Upload" 
        content={<ManualUploadInner handleTriggerConversion={props.handleTriggerConversion} handleSetStatus={props.handleSetStatus} requestWithAuth={requestWithAuth} />} 
      />
    )
}

const ManualUploadInner = (props) => {
  const { requestWithAuth } = props;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [interior, setInterior] = useState([]);
  const [cover, setCover] = useState([]);
  const [copyright, setCopyright] = useState([]);
  const [note, setNote] = useState("");
  const [version, setVersion] = useState("1.0");
  const [watermark, setWatermark] = useState("");
  const [watermarkc4, setWatermarkc4] = useState("");
  const [replacements, setReplacements] = useState([]);
  const [showReplacements, setShowReplacements] = useState(false);
  const [metadata, setMetadata] = useState({
    title_long: "", 
    author_yomigana: "", 
    eisbn: "", 
    pisbn: "",
    ltr: false
  });
  const [conversionSettings, setConversionSettings] = useState({
    pdfOnly: false, 
    epubOnly: false, 
    coverOnly: false, 
    noCover: false,
    pdf_has_cover: false
  });

  const updateMetadata = (updated) => {
    if (updated.hasOwnProperty("eisbn")) {
      setError("");
    }
    setMetadata({...metadata, ...updated});
  }

  const updateConversionSettings = (updated) => {
    setConversionSettings({...conversionSettings, ...updated});
  }

  const updateReplacements = (updated) => {
    setReplacements([...replacements, updated]);
  }

  const removeReplacement = (number) => {
    let updated = replacements.filter(r => r.page !== number);
    setReplacements(updated);
  }

  const runConversion = async () => {
    // create book record (validating for existence first)
    const res = await requestWithAuth("post", `${env.BASE_URL}/api/books/none/validate_and_create_book/`, metadata);
    if (res && res.data) {
      if (res.data.status === "fail") {
        setError(res.data.message);
      } else {
        props.handleSetStatus({
          conversion_id: "manual001", 
          status: "UPLOADING...",
          eisbn: metadata.eisbn,
          title_long: metadata.title_long,
          message: "Uploading files..."
        });
      }
    }
    // create item object
    let item = {...metadata};
    if (copyright.length > 0) {
      item["copyright"] = copyright[0].path;
      item["copyright_file"] = copyright[0];
    }
    if (cover.length > 0) {
      item["cover"] = cover[0].path;
      item["cover_file"] = cover[0];
    }
    if (interior.length > 0) {
      item["interior"] = interior[0].path;
      item["interior_file"] = interior[0];
    }
    if (replacements.length > 0) {
      item["replacements"] = replacements;
    }
    item["version"] = version;
    let mySettings = {...conversionSettings};
    // watermarks get added to ui_params in backend/views.py
    if (watermark) {
      conversionSettings.watermark = watermark;
    }
    if (watermarkc4) {
      conversionSettings.watermark_c4 = watermarkc4;
    }
    // trigger conversion
    if (item.hasOwnProperty("interior") || conversionSettings.coverOnly === true) {
      props.handleTriggerConversion([item, note, conversionSettings, "manual001"]);
    }
    // reset fields
    setMetadata({
      title_long: "", 
      author_yomigana: "", 
      eisbn: "", 
      pisbn: "",
      ltr: false
    });
    setConversionSettings({
      pdfOnly: false, 
      epubOnly: false, 
      coverOnly: false, 
      noCover: false,
      pdf_has_cover: false
    });
    setInterior([]);
    setCover([]);
    setCopyright([]);
    setReplacements([]);
    setVersion("1.0");
    setNote("");
    setWatermark("");
    setWatermarkc4("");
  }

  let howmany = replacements.length > 0 ? "another" : "a";

  let metadata_disabled = !metadata.eisbn || metadata.eisbn.length > 13 || metadata.pisbn.length > 13;

  return (
    <Pane
      display="flex"
      flex-direction="row"
      justifyContent="space-around"
      alignItems="flex-start"
      background="tint1"
      padding={12}
    >
      <Pane marginRight={16}>
        <DropFile label="Interior PDF..." type="pdf" handleFileData={setInterior} pdfFiles={interior} compact={true} />
        <DropFile label="Cover..." type="cover" handleFileData={setCover} pdfFiles={cover} compact={true} />
        <DropFile label="Copyright..." type="pdf" handleFileData={setCopyright} pdfFiles={copyright} compact={true} />
        <Pane marginY={16} display="flex" flexDirection="column">
          <TextInput 
            width="100%"
            placeholder="C1 Watermark text (leave blank for none)"
            value={watermark === "default" ? "(default)" : watermark} 
            onChange={e => {
              setWatermark(e.target.value);
            }}
          />
          <Checkbox
            marginTop={4}
            label="Use default C1 watermark text"
            checked={watermark === "default"}
            onChange={e => {
              let newVal = watermark === "default" ? "" : "default";
              setWatermark(prev => newVal);
            }}
          />
        </Pane>
        <Pane marginY={16} display="flex" flexDirection="column">
          <TextInput 
            width="100%"
            placeholder="C4 Watermark text (leave blank for none)"
            value={watermarkc4 === "default" ? "(default)" : watermarkc4} 
            onChange={e => {
              setWatermarkc4(e.target.value);
            }}
          />
          <Checkbox
            marginTop={4}
            label="Use default C4 watermark text"
            checked={watermarkc4 === "default"}
            onChange={e => {
              let newVal = watermarkc4 === "default" ? "" : "default";
              setWatermarkc4(prev => newVal);
            }}
          />
        </Pane>
        {replacements.map(r => {
          if (r.mode === "replace") {
            return <ReplacePage data={r} handleRemoveReplacement={removeReplacement} key={r.page} />
          } else {
            return <InsertPage data={r} handleRemoveReplacement={removeReplacement} key={r.page} />
          }
        })}
        <Button onClick={e => setShowReplacements(true)}><PlusIcon marginRight={12} /> Add {howmany} custom page...</Button>
        <CustomPages handleSetShown={setShowReplacements} show={showReplacements} handleAddCustomPage={updateReplacements} />
      </Pane>
      <Pane>
        {error && (<Paragraph maxWidth="340px" size={300} color="red">{error}</Paragraph>)}
        <Pane>
          <MetadataForm updateMetadata={updateMetadata} metadata={metadata} />
          <Paragraph maxWidth="340px" size={300} marginTop={8}>
          Note: If a book with this eISBN already exists, 
          it's metadata will be overwritten with the values you type here. 
          To avoid retyping the metadata, you can re-convert existing 
          books in the Book record on the Books & Downloads tab.
          </Paragraph>
        </Pane>
        <VersionForm version={version} updateVersion={setVersion} />
        <ConversionSettings updateConversionSettings={updateConversionSettings} />
        <Note value={note} handleChange={setNote} handleRemoveNote={e => setNote("")} />
        <Button disabled={metadata_disabled} onClick={e => runConversion()} marginTop={12} appearance="primary" intent="success" size="large">Go</Button>
      </Pane>
    </Pane>
  )
}

export default ManualUpload;
